'use strict'

// A key is claimed with `undefined` the moment its fetch starts, so `has` blocks
// the render burst that follows while `get` still reports it unknown. The render
// path is synchronous, so the claim cannot be raced.
const cache = new Map()

const PHASES = ['draft', 'ready-for-review', 'in-review', 'approved', 'implemented']
const PR_STATES = ['open', 'merged', 'closed']

// The note's own URL segment, which is what /api/note takes and what stays put
// for the life of the page. Not `noteid` from lib/config: that is
// pathname.split('/')[1], which on a published /s/<id> view is the literal 's'.
function specNoteKey (pathname, urlpath) {
  let p = String(pathname || '')
  if (urlpath && p.indexOf('/' + urlpath) === 0) p = p.slice(urlpath.length + 1)
  const segs = p.split('/').filter(Boolean)
  if (!segs.length) return ''
  const id = (segs[0] === 's' || segs[0] === 'p') ? (segs[1] || '') : segs[0]
  // ponytail: same charset the board's route accepts. A freeURL alias outside it
  // simply never asks, and the header stays on frontmatter.
  return /^[\w-]{1,128}$/.test(id) ? id : ''
}

// undefined = unknown, still or forever; null = asked and the board had nothing.
function getSpecRecord (key) {
  return cache.get(key)
}

// Every failure settles to null and stays there. Retrying inside a render loop
// turns a board outage into a flood; a reload is the retry.
function fetchSpecRecord (key, boardURL, onSettled) {
  if (!key || !boardURL || cache.has(key)) return
  if (typeof fetch !== 'function') return
  cache.set(key, undefined)
  const url = String(boardURL).replace(/\/+$/, '') + '/api/note/' + key
  fetch(url, { credentials: 'omit', mode: 'cors' })
    .then(r => (r.ok ? r.json() : null))
    .catch(() => null)
    .then(json => {
      cache.set(key, normalizeSpecRecord(json))
      if (typeof onSettled === 'function') onSettled(key)
    })
}

const LOGIN = /^[\w.-]{1,64}$/
// A board path, never a full url: the board origin is configuration, and a
// record must not be able to point a link elsewhere.
const CHANGES = /^\/changes\/[\w-]{1,128}$/
const count = v => (Number.isInteger(Number(v)) && Number(v) >= 0 ? Number(v) : 0)
const logins = v => (Array.isArray(v) ? v.filter(a => typeof a === 'string' && LOGIN.test(a)) : null)

// An allowlist rather than a passthrough: `status` reaches a class name, where
// escaping is not the defence. A bad field degrades itself, not the record.
// approvedBy is the board's attested approvers, the names whose owners wrote
// them; null when the board predates attestation, so the list stands as is.
function normalizeSpecRecord (json) {
  if (!json || typeof json !== 'object') return null
  const pr = Number(json.pr)
  return {
    status: PHASES.indexOf(json.status) !== -1 ? json.status : '',
    area: typeof json.area === 'string' ? json.area.trim() : '',
    namespace: typeof json.namespace === 'string' ? json.namespace.trim() : '',
    pr: Number.isInteger(pr) && pr > 0 && pr < 1e6 ? pr : 0,
    prState: PR_STATES.indexOf(json.prState) !== -1 ? json.prState : 'open',
    approvedBy: logins(json.approvedBy),
    approvals: count(json.approvals),
    required: count(json.required),
    // Approvers the text moved past, and the board page that shows the diff.
    stale: logins(json.stale) || [],
    changesUrl: typeof json.changesUrl === 'string' && CHANGES.test(json.changesUrl) ? json.changesUrl : ''
  }
}

// Reasons the board gives, in the words a toast can show.
const ERRORS = {
  401: 'The editor and the board do not share a secret',
  403: 'You are not an approver in roles.yml',
  404: 'The board does not know this note yet; try again after its next poll',
  409: 'The spec is not under review',
  412: 'The note changed while approval was being recorded; review it and try again',
  503: 'The board cannot record approvals: no EDITOR_SECRET'
}

// Records or retracts this session's approval on the board: the editor signs
// who we are, the board keeps the approval and answers with the note's fresh
// record, which replaces the cached one. Rejects with a message to show.
async function prepareSpecApproval (getContent, isSynchronized) {
  const content = getContent()
  const deadline = Date.now() + 15000
  while (!isSynchronized()) {
    if (getContent() !== content) throw new Error('The note changed; review it before approving')
    if (Date.now() >= deadline) throw new Error('Waiting for the editor to reconnect and save; try again shortly')
    await new Promise(resolve => setTimeout(resolve, 50))
  }
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(content))
  if (getContent() !== content || !isSynchronized()) throw new Error('The note changed; review it before approving')
  return { content, expectedHash: Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2, '0')).join('') }
}

function postSpecApproval (key, boardURL, editorURL, action, expectedHash, isCurrent = () => true) {
  if (!key || !boardURL) return Promise.reject(new Error('No board configured'))
  if (!/^[a-f0-9]{64}$/.test(expectedHash || '')) return Promise.reject(new Error('Save the current note before approving'))
  const board = String(boardURL).replace(/\/+$/, '')
  return fetch(String(editorURL || '').replace(/\/+$/, '') + '/me/assert', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ version: 1, noteId: key, action, expectedHash })
  })
    .then(r => {
      if (r.ok) return r.json()
      if (r.status === 404) throw new Error('The editor has no CMD_SPEC_BOARD_SECRET')
      if (r.status === 412) throw new Error(ERRORS[412])
      if (r.status >= 500) throw new Error('The editor could not save the note; try again shortly')
      throw new Error('Sign in with GitHub to approve')
    })
    .then(me => {
      if (!isCurrent()) throw new Error('The note changed; review it before approving')
      return fetch(board + '/api/note/' + key + '/approvals', {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token: me.token, action })
      })
    })
    .then(r => {
      if (!r.ok) throw new Error(ERRORS[r.status] || 'The board refused the approval (' + r.status + ')')
      return r.json()
    })
    .then(json => {
      const rec = normalizeSpecRecord(json)
      cache.set(key, rec)
      return rec
    })
}

module.exports = { specNoteKey, getSpecRecord, fetchSpecRecord, normalizeSpecRecord, postSpecApproval, prepareSpecApproval }
